import { defineStore } from 'pinia'
import { reactive, ref, watch, toRaw } from 'vue'
import { useAnnotationsStore } from './annotationsStore'
import { useServerStore } from './serverStore'
import { getDefectStrokeColor } from 'Modules/pages/imageReview/helperFunctions'
import { ImageAnnotationType } from 'Modules/types/pages/imageReview/imageAnnotationType'
import { DefectTypes } from 'Modules/types/pages/imageReview/enums'
import { BaseMarker } from 'Common/utils/markerArea/types'
import { MARKER_HOVER_STROKE_COLOR, MARKER_SELECTED_STROKE_COLOR } from 'Modules/pages/imageReview/constants'
import { MARKER_DEFAULT_STROKE_COLOR } from 'Common/utils/markerArea/constants'
import { useKeyboardStore } from 'Store/keyboardStore'

export const useMarkerStore = defineStore('ImageReview_markers', () => {
  // State
  const markersMap = reactive(new Map<string, BaseMarker<any, any, any, any>>())
  const hoveredMarkers = ref<Array<BaseMarker<any, any, any, any>>>([])
  const selectedMarkers = ref<Array<BaseMarker<any, any, any, any>>>([])
  const boundUpdatedMarkers = ref<Array<BaseMarker<any, any, any, any>>>([])

  const keyboardStore = useKeyboardStore()
  const annotationsStore = useAnnotationsStore()
  const serverStore = useServerStore()

  //Actions
  function setMarkerStrokeCategoryColor(annotation: ImageAnnotationType) {
    const marker = markersMap.get(annotation.annotationUuid)
    const mappedDefectValue = DefectTypes[annotation.defectType]
    const defectStrokeColor = getDefectStrokeColor(mappedDefectValue)
    marker.state.meta.strokeCategoryColor = defectStrokeColor || MARKER_DEFAULT_STROKE_COLOR
  }

  function setMarkerDashStyling(annotation: ImageAnnotationType) {
    const marker = markersMap.get(annotation.annotationUuid)
    if (annotation.isAnnotation && marker.state.isDashed === true) {
      marker.state.isDashed = false
    } else if (!annotation.isAnnotation) {
      marker.state.isDashed = true
    }
  }

  function createMarkerEntry(markerKey: string, marker: BaseMarker<any, any, any, any>) {
    if (markerKey && marker) {
      markersMap.set(markerKey, marker)
    }
  }

  function getMarkerEntry(markerKey: string): BaseMarker<any, any, any, any> {
    return markersMap.get(markerKey)
  }

  function deleteMarkerEntry(markerKey: string) {
    markersMap.delete(markerKey)
  }

  function clearMarkerEntries() {
    markersMap.clear()
    hoveredMarkers.value = []
    selectedMarkers.value = []
    boundUpdatedMarkers.value = []
  }

  function syncMarkerEntry(annotation: ImageAnnotationType) {
    const marker = markersMap.get(annotation.annotationUuid)
    setMarkerStrokeCategoryColor(annotation)
    setMarkerDashStyling(annotation)
    removeBoundUpdatedMarker(marker)
    if (!marker.state.isSelected && !marker.state.isHovered)
      marker.state.strokeColor = marker.state.meta.defectStrokeColor
  }

  function syncMarkerEntries(annotations: Array<ImageAnnotationType>) {
    annotations.forEach((annotation) => {
      syncMarkerEntry(annotation)
    })
  }

  function updateMarkerKey(markerKey: string, newMarkerKey: string) {
    if (markerKey === newMarkerKey) return

    const marker = getMarkerEntry(markerKey)
    if (marker) {
      markersMap.set(newMarkerKey, marker)
      markersMap.delete(markerKey)
      marker.state.meta.id = newMarkerKey
    }
  }

  function getMarkerKey(marker: BaseMarker<any, any, any, any>) {
    for (const [key, mappedMarker] of markersMap.entries()) {
      if (mappedMarker === marker) {
        return key
      }
    }
    return undefined
  }

  function getMarkersArray(): Array<any> {
    return Array.from(markersMap.entries())
  }

  function getMarkersInGroup(marker: BaseMarker<any, any, any, any>) {
    const markerId = marker.state.meta.id
    const matchingAnnotation = annotationsStore.annotations.find((annotation) => annotation.annotationUuid === markerId)
    if (matchingAnnotation?.annotationGroup === undefined) return [marker]

    const annotationsInGroup = annotationsStore.groupedAnnotationsMap[matchingAnnotation.annotationGroup]
    const markersInGroup = []

    if (Array.isArray(annotationsInGroup) && annotationsInGroup.length > 0) {
      annotationsInGroup.forEach((annotation) => {
        markersInGroup.push(getMarkerEntry(annotation.annotationUuid))
      })
    } else {
      markersInGroup.push(marker)
    }
    return markersInGroup
  }

  function addHoveredMarker(marker: BaseMarker<any, any, any, any>) {
    if (keyboardStore.isCtrlPressed === true) {
      const existsInHovered = hoveredMarkers.value.some((_marker) => _marker === marker)
      if (!existsInHovered) hoveredMarkers.value = [...hoveredMarkers.value, marker]
      return
    }

    const markersInGroup = getMarkersInGroup(marker)
    const markersToAppend = []

    markersInGroup.forEach((marker) => {
      const existsInHovered = hoveredMarkers.value.some((_marker) => _marker === marker)
      if (!existsInHovered) markersToAppend.push(toRaw(marker))
    })

    if (markersToAppend.length > 0) {
      hoveredMarkers.value = [...hoveredMarkers.value, ...markersToAppend]
    }
  }

  function removeHoveredMarker(marker: BaseMarker<any, any, any, any>) {
    const markersInGroup = getMarkersInGroup(marker)

    if (markersInGroup.length > 1) {
      const allMarkersInGroupNotHovered = markersInGroup.every((groupedMarker) => !groupedMarker.state.isHovered)

      if (allMarkersInGroupNotHovered) {
        const markersToRemoveSet = new Set(markersInGroup.map((marker) => marker.state.meta.id))
        hoveredMarkers.value = hoveredMarkers.value.filter(
          (hoveredMarker) => !markersToRemoveSet.has(hoveredMarker.state.meta.id)
        )
      }
    } else {
      hoveredMarkers.value = hoveredMarkers.value.filter((hoveredMarker) => {
        return toRaw(hoveredMarker) !== marker
      })
    }
  }

  function addBoundUpdatedMarker(marker: BaseMarker<any, any, any, any>) {
    const matchingMappedMarker = getMarkerEntry(marker.state.meta.id)
    const exists = boundUpdatedMarkers.value.some((_marker) => _marker === matchingMappedMarker)
    if (exists) {
      removeBoundUpdatedMarker(marker)
    }
    if (matchingMappedMarker) {
      boundUpdatedMarkers.value = [...boundUpdatedMarkers.value, matchingMappedMarker]
    }
  }

  function removeBoundUpdatedMarker(marker: BaseMarker<any, any, any, any>) {
    const matchingMappedMarker = getMarkerEntry(marker.state.meta.id)
    boundUpdatedMarkers.value = Array.from(
      boundUpdatedMarkers.value.filter((_marker) => _marker !== matchingMappedMarker)
    )
  }

  function addSelectedMarker(marker: BaseMarker<any, any, any, any>) {
    if (keyboardStore.isCtrlPressed === true) {
      const existsInSelected = selectedMarkers.value.some((_marker) => _marker === marker)
      if (!existsInSelected && marker) {
        selectedMarkers.value = [...selectedMarkers.value, marker]
      }
      return
    }

    const markersInGroup = getMarkersInGroup(marker)
    const markersToAppend = []

    if (markersInGroup.every((_marker) => selectedMarkers.value.includes(_marker))) return

    markersInGroup.forEach((marker) => {
      const existsInSelected = selectedMarkers.value.some((_marker) => _marker === marker)
      //FIXME Bug in marker selection
      //Steps to reproduce:
      //- Select markers in group
      //- Select markers not in group
      //- Deselect all markers (i.e. release ctrl + click outside any marker bound)
      //- Select markers in group by clicking one of the markers in group (don't hold ctrl as this will select single marker)
      //- Note: Not all markers in group become selected; after leaving marker bounds it becomes deselected

      //FIXME figure out better approach instead of having to call select marker again
      //as it will call this function again. Guard clause above can be removed as well
      //once better approach is implemented.
      //Current problem is that we want marker event to bubble to markerArea
      //Guard clause could be added in defect event listeners?
      //Though that isn't the clearest when following the code.

      //TODO evaluate adding marker grouping directly into marker area / markers
      if (marker.state.isSelected === false) marker.controller.selectMarker()
      if (!existsInSelected) markersToAppend.push(marker)
    })

    if (markersToAppend.length > 0) {
      selectedMarkers.value = [...selectedMarkers.value, ...markersToAppend]
    }
  }

  function removeSelectedMarker(marker: BaseMarker<any, any, any, any>) {
    const markersInGroup = getMarkersInGroup(marker)
    if (markersInGroup.length > 1) {
      markersInGroup.forEach((groupedMarker) => {
        selectedMarkers.value = selectedMarkers.value.filter((selectedMarker) => selectedMarker === groupedMarker)
      })
    } else {
      selectedMarkers.value = selectedMarkers.value.filter((_marker) => _marker === marker)
    }
  }

  watch(
    () => annotationsStore.annotationsData,
    () => {
      //Markers update annotation isHovered, which in turn updates the stroke color for markers
      //Keeps annotations as the single source of truth for when to update marker strokes
      const markersEntries = getMarkersArray()
      markersEntries.forEach((entry) => {
        const [markerKey, marker] = entry
        const isMarkerPendingCreation = serverStore.createImageAnnotation.isLoading
        const isMeasurementMarker =
          marker.state.meta?.isMeasurement !== undefined && marker.state.meta.isMeasurement !== true
        if (isMeasurementMarker) return
        //Check annotationData first, as most likely a annotation committed to backend
        //Check stagedAnnotation second, as new annotations are not yet committed and won't be in annotationsData
        const correspondingAnnotation =
          annotationsStore.annotationsData.find(
            (annotation) => annotation.annotationUuid === markerKey || annotation?.tempAnnotationUuid === markerKey
          ) || annotationsStore.stagedAnnotations.find((annotation) => annotation?.annotationUuid === markerKey)

        const isCorrespondingAnnotationStaged =
          annotationsStore.stagedAnnotations.find(
            (annotation) => annotation.annotationUuid === correspondingAnnotation?.annotationUuid
          ) !== undefined

        //FIXME: annotation with temp key still exists - updateMarkerKey gets calls unneccesarily
        //Guard clause added to updateMarkerKey to return early for now
        if (correspondingAnnotation?.tempAnnotationUuid !== undefined) {
          updateMarkerKey(markerKey, correspondingAnnotation.annotationUuid)
        }
        if (!isCorrespondingAnnotationStaged) {
          marker.state.isSelectionLocked = false
          marker.controller.deselectMarker()
        }
        if (correspondingAnnotation?.isNew) {
          syncMarkerEntry(correspondingAnnotation)
        }
        //Check pending creation first and markers being persisted are not in annotationsData or stagedAnnotations
        //Could cause race condition if 2+ create marker calls are occuring
        if (!isMarkerPendingCreation && !correspondingAnnotation) deleteMarkerEntry(markerKey)
      })

      annotationsStore.annotationsData.forEach((annotation) => {
        const correspondingMarker = getMarkerEntry(annotation.annotationUuid)
        //Update stroke color
        if (correspondingMarker) {
          if (correspondingMarker.state.isSelected === false) {
            if (annotation.isHovered === true) {
              correspondingMarker.state.strokeColor = MARKER_HOVER_STROKE_COLOR
            } else {
              correspondingMarker.state.strokeColor = correspondingMarker.state.meta.strokeCategoryColor
            }
          }
        }
      })
    },
    { deep: true }
  )

  watch(
    () => annotationsStore.stagedAnnotations,
    () => {
      const markersEntries = getMarkersArray()
      markersEntries.forEach((entry) => {
        const [markerKey, marker] = entry
        const correspondingAnnotation = annotationsStore.stagedAnnotations.find(
          (annotation) => annotation.annotationUuid === markerKey
        )
        //Newly created annotation markers do not have strokeCategoryColor assigned to them
        if (correspondingAnnotation?.isTemp && !marker.state.meta?.strokeCategoryColor) {
          setMarkerStrokeCategoryColor(correspondingAnnotation)
        }
        if (!serverStore.createImageMeasurement.isLoading && !correspondingAnnotation && marker.state.isSelected) {
          marker.controller.deselectMarker()
          marker.strokeColor = marker.state.meta.strokeCategoryColor
        }
      })

      annotationsStore.stagedAnnotations.forEach((annotation) => {
        const correspondingMarker = markersMap.get(annotation.annotationUuid)
        if (correspondingMarker) {
          correspondingMarker.controller.selectMarker()
          correspondingMarker.state.strokeColor = MARKER_SELECTED_STROKE_COLOR
        }
      })
    }
  )

  watch(
    () => keyboardStore.isCtrlPressed,
    () => {
      const firstHoveredMarker = hoveredMarkers.value[0]
      //FIXME user could be hovered over nested markers in which case this will not behave accordingly.
      if (keyboardStore.isCtrlPressed && hoveredMarkers.value.length > 1) {
        //Grouped markers will get added to hovered and selected marker state together
        //Except when ctrl is pressed it allows for a single marker selection selection
        //regardless if user is hovered over group; this syncs the hovered markers stroke
        //to reflect single marker selection
        hoveredMarkers.value = hoveredMarkers.value.filter((marker) => marker.isMousedOver() === true)
      } else if (firstHoveredMarker) {
        //If user releases ctrl and has not yet selected a marker in the group
        //add any markers in group back into hovered markers.
        if (firstHoveredMarker.state.isSelected === false) addHoveredMarker(hoveredMarkers.value[0])
      }
    }
  )

  return {
    markersMap,
    hoveredMarkers,
    selectedMarkers,
    boundUpdatedMarkers,
    createMarkerEntry,
    getMarkerEntry,
    deleteMarkerEntry,
    clearMarkerEntries,
    syncMarkerEntry,
    syncMarkerEntries,
    updateMarkerKey,
    getMarkerKey,
    addHoveredMarker,
    removeHoveredMarker,
    addBoundUpdatedMarker,
    removeBoundUpdatedMarker,
    addSelectedMarker,
    removeSelectedMarker,
  }
})
